import { useTheme } from "next-themes"
import { Toaster as Sonner, toast } from "sonner"
import { CircleAlert, CircleCheck, Info, TriangleAlert } from "lucide-react"

type ToasterProps = React.ComponentProps<typeof Sonner>

/**
 * The one toast look (DESIGN.md §10: one toast system), 2026-10-15. Jota, on the
 * mirror's "Saved" toast: "so does the saved after changing on or off ... looks
 * weird". The action was a filled brand button, the loudest thing on screen for
 * a secondary act; the success icon was the library's black disc, outside the
 * app's icon set; and the stack sat over the Feedback button (bottom-6 right-6,
 * 40 px). Now: the action is a quiet text button in the brand colour, the icons
 * are the app's own (lucide, currentColor, so they take the toast's own text
 * colour), and the stack starts above the button: 24 + 40 + 12 = 76 px at a
 * 16 px root. The offsets are rem, as the button is (bottom-6, h-10), so inside
 * the app at 90 % (src/lib/appSurface.ts) the two still meet: 1.5 + 2.5 + 0.75.
 */
const ICON = "h-4 w-4"
const ABOVE_FEEDBACK_BUTTON = "4.75rem"

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme()

  return (
    <Sonner
      theme={theme as ToasterProps["theme"]}
      className="toaster group"
      offset={{ bottom: ABOVE_FEEDBACK_BUTTON, right: "1.5rem" }}
      mobileOffset={{ bottom: ABOVE_FEEDBACK_BUTTON, left: "1rem", right: "1rem" }}
      icons={{
        success: <CircleCheck className={ICON} aria-hidden="true" />,
        info: <Info className={ICON} aria-hidden="true" />,
        warning: <TriangleAlert className={ICON} aria-hidden="true" />,
        error: <CircleAlert className={ICON} aria-hidden="true" />,
      }}
      toastOptions={{
        classNames: {
          toast:
            "group toast group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg",
          description: "group-[.toast]:text-muted-foreground",
          // The toast class above forces the neutral card; an error keeps the
          // red card the shadcn 'destructive' variant had. `!` wins the tie at
          // equal specificity (both classes sit on the same element).
          error:
            "group-[.toaster]:!bg-destructive group-[.toaster]:!text-destructive-foreground group-[.toaster]:!border-destructive",
          // A text button: the brand colour on the neutral card, the toast's own
          // text colour on the red one. `!` beats sonner's filled default.
          actionButton:
            "group-[.toast]:!bg-transparent group-[.toast]:!text-primary group-[.toast]:!font-medium group-[.toast]:!px-1.5 group-[.toast]:hover:!underline group-[.toast]:underline-offset-4 group-data-[type=error]:!text-destructive-foreground",
          cancelButton:
            "group-[.toast]:bg-muted group-[.toast]:text-muted-foreground",
        },
      }}
      {...props}
    />
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export { Toaster, toast }
